import { ref, computed, unref, type Ref, type ComputedRef, type Component } from 'vue';
import { isDefined, isEmpty, isObject } from 'class-validator';
import {
  CustomFieldType,
  CustomFormFlowEntitiesEnum,
  AppsEnum,
  AppointmentStatus,
  CustomFormFlowCategoryEnum,
  getCustomFieldFormattedValue
} from '@nova/core';
import customFormsService from '@satellite/services/custom-forms-service.js';
import assetVisitService from '@satellite/services/asset-visit-service.js';
import { CUSTOM_FIELD_COMPONENTS } from '@satellite/components/custom_forms/field_types';
import { useHttpClient } from './useHttpClient';
import type {
  UseCustomFormsOptions,
  UseCustomFormsReturn,
  ITrigger,
  IFlow,
  CustomFormDataEntry,
  FormFieldLike
} from '@satellite/components/custom_forms/types';
import type { ICustomField } from '@nova/core';

/** Map from CustomFieldType to component name for dynamic rendering. */
const CUSTOM_FIELD_COMPONENT_NAMES: Record<CustomFieldType, string> = {
  [CustomFieldType.String]: 'custom-field-string',
  [CustomFieldType.BigString]: 'custom-field-bigstring',
  [CustomFieldType.Number]: 'custom-field-number',
  [CustomFieldType.Bool]: 'custom-field-bool',
  [CustomFieldType.Date]: 'custom-field-date',
  [CustomFieldType.Timestamp]: 'custom-field-timestamp',
  [CustomFieldType.Email]: 'custom-field-email',
  [CustomFieldType.Phone]: 'custom-field-phone',
  [CustomFieldType.DropDown]: 'custom-field-dropdown',
  [CustomFieldType.DropDownMultiSelect]: 'custom-field-dropdownmultiselect',
  [CustomFieldType.ComboBox]: 'custom-field-combobox',
  [CustomFieldType.Document]: 'custom-field-document',
  [CustomFieldType.MultiDocument]: 'custom-field-multidocument',
  [CustomFieldType.Action]: 'custom-field-action'
};

/**
 * Composable for custom forms state management and CRUD operations.
 *
 * Manages:
 * - Active trigger selection
 * - Form data state (keyed by triggerId)
 * - Load/save form data via custom-forms-service and useHttpClient
 * - Trigger-on-status checks and action field handling
 *
 * @param options - Configuration (timezone, militaryTimeEnabled, triggers, onNotify, onCustomFormsDataChange)
 * @returns Custom forms state and methods
 *
 * @example
 * ```ts
 * const { activeTrigger, getCustomFormsData, updateCustomFormsData } = useCustomForms({
 *   timezone: 'America/New_York',
 *   militaryTimeEnabled: false,
 *   triggers: warehouseTriggers,
 *   onNotify: (msg, type) => notify(msg, type),
 *   onCustomFormsDataChange: () => eventHub.emit('customformdata-updated')
 * });
 * ```
 */
export function useCustomForms(options: UseCustomFormsOptions = {}): UseCustomFormsReturn {
  const httpClient = useHttpClient();

  const triggersRef = () => unref(options.triggers);

  const activeTrigger: Ref<ITrigger | null> = ref(null);
  const customFormsData: Ref<Record<string, CustomFormDataEntry[]>> = ref({});
  const pendingStatusChange: Ref<unknown> = ref(null);

  const activeFlow: ComputedRef<IFlow | null> = computed(() => activeTrigger.value?.flow ?? null);

  const customFieldComponents: ComputedRef<Record<string, string>> = computed(
    () => CUSTOM_FIELD_COMPONENT_NAMES
  );

  const hasActiveTrigger: ComputedRef<boolean> = computed(() => isObject(activeTrigger.value));

  const hasCustomFormsData: ComputedRef<boolean> = computed(() => {
    const data = customFormsData.value as Record<string, unknown> & { skip?: boolean };
    if (data?.skip === true) return true;
    if (!data || typeof data !== 'object') return false;
    return Object.values(data).some(arr => Array.isArray(arr) && arr.length > 0);
  });

  const customFormsComponentsMapping: ComputedRef<Record<string, { component: Component }>> =
    computed(() => {
      const mapping: Record<string, { component: Component }> = {};
      Object.entries(CUSTOM_FIELD_COMPONENTS).forEach(([type, component]) => {
        mapping[type] = { component };
      });
      return mapping;
    });

  const getFormattedValue = (field: CustomFormDataEntry): string => {
    const value = field.value;
    if (
      value === '' ||
      !isDefined(value) ||
      isEmpty(value) ||
      (Array.isArray(value) && value.length === 0)
    ) {
      return '--';
    }
    const type = (field.formField as { field?: { type: CustomFieldType } })?.field?.type;
    const customField = { type, value } as Parameters<typeof getCustomFieldFormattedValue>[0];
    const result = getCustomFieldFormattedValue(customField, {
      [CustomFieldType.Document]: { generateLink: true },
      [CustomFieldType.MultiDocument]: { generateLink: true },
      [CustomFieldType.Timestamp]: {
        timezone: unref(options.timezone) ?? '',
        formatAsMilitary: unref(options.militaryTimeEnabled) ?? false
      }
    });
    return result != null ? String(result) : '--';
  };

  const getCustomFormsData = async (
    objectId: string,
    triggerId: string,
    lastChangedDateTimeOnly = false
  ): Promise<void | string> => {
    const params: Record<string, unknown> = {
      s: { objectId, triggerId },
      limit: lastChangedDateTimeOnly ? 1 : 1000
    };
    if (lastChangedDateTimeOnly) {
      params.fields = 'id,lastChangedDateTime,formField.id';
    }
    if (!objectId || !triggerId) {
      return;
    }
    const response = await customFormsService.getCustomFormData(
      params as Parameters<typeof customFormsService.getCustomFormData>[0],
      {},
      {},
      httpClient
    );
    const data = response as CustomFormDataEntry[];

    if (lastChangedDateTimeOnly && data.length > 0) {
      return (data[0] as { lastChangedDateTime?: string })?.lastChangedDateTime;
    }

    const formatted = data.map((row: CustomFormDataEntry) => {
      const label = (row.formField as { overrideLabel?: string })?.overrideLabel ?? row.label;
      const entry = { ...row, label };
      return {
        ...entry,
        value: getFormattedValue(entry)
      };
    });

    customFormsData.value[triggerId] = formatted;
  };

  const getCustomField = (formField: FormFieldLike): ICustomField =>
    ({
      name: formField.id,
      type: formField.field.type,
      label: formField.overrideLabel ?? formField.field.label ?? '',
      order: formField.order,
      description: formField.field.description,
      placeholder: formField.field.placeholder,
      required: formField.required ?? false,
      dropDownValues: formField.field.extraFields?.dropDownValues,
      minLengthOrValue: formField.field.extraFields?.minLengthOrValue ?? undefined,
      maxLengthOrValue: formField.field.extraFields?.maxLengthOrValue ?? undefined
    } as ICustomField);

  const createBulkCustomFormsData = async (
    value: CustomFormDataEntry[],
    warehouseId: string
  ): Promise<void> => {
    if (!Array.isArray(value)) return;
    const firstSubmit = value.every(formData => !formData.id);
    if (firstSubmit) {
      await httpClient.post('custom-forms/form-data/bulk', {
        bulk: value,
        warehouseId
      });
      options.onNotify?.('Appointment data has been updated', 'success');
      customFormsDataChange();
    } else {
      await updateCustomFormsData(value);
    }
  };

  const updateCustomFormsData = async (value: CustomFormDataEntry[]): Promise<void> => {
    const tasks: Array<{
      index: number;
      formData: CustomFormDataEntry;
      promise: Promise<unknown>;
    }> = [];
    value.forEach((formData, index) => {
      if (formData.id) {
        tasks.push({
          index,
          formData,
          promise: httpClient.patch(`custom-forms/form-data/${formData.id}`, {
            value: formData.value
          })
        });
      } else if (formData.value !== null && formData.value !== undefined) {
        tasks.push({
          index,
          formData,
          promise: httpClient.post('custom-forms/form-data', { ...formData })
        });
      }
    });

    const failures: Array<{ index: number; id?: string; error: string }> = [];

    if (tasks.length > 0) {
      try {
        const settled = await Promise.allSettled(tasks.map(t => t.promise));
        settled.forEach((result, i) => {
          const task = tasks[i];
          if (result.status === 'rejected' && task) {
            failures.push({
              index: task.index,
              id: task.formData.id,
              error: result.reason instanceof Error ? result.reason.message : String(result.reason)
            });
          }
        });
      } catch (err) {
        const reason = err instanceof Error ? err.message : String(err);
        tasks.forEach(task => {
          failures.push({ index: task.index, id: task.formData.id, error: reason });
        });
      }
    }

    const allSucceeded = failures.length === 0;

    if (allSucceeded && tasks.length > 0) {
      options.onNotify?.('Appointment data has been updated', 'success');
    } else if (failures.length > 0) {
      const message = failures.map(f => `Entry ${f.id ?? f.index}: ${f.error}`).join('; ');
      options.onNotify?.(message, 'error');
    }
    customFormsDataChange();
  };

  const handleActionFieldTriggered = (actionName: string): void => {
    if (!actionName) return;
    const triggers = triggersRef();
    const featureTrigger = Array.isArray(triggers)
      ? triggers.find((t: ITrigger) => t.feature === actionName)
      : undefined;
    if (featureTrigger) {
      const triggerWithActive = featureTrigger as ITrigger & { isActive?: boolean };
      if (triggerWithActive.isActive !== false && isDefined(featureTrigger.flow)) {
        setTimeout(() => {
          activeTrigger.value = featureTrigger;
        }, 600);
      } else {
        options.onNotify?.('Feature is not enabled', 'warning');
      }
    } else {
      options.onNotify?.(`Feature not found for action ${actionName}`, 'warning');
    }
  };

  const customFormsDataChange = (): void => {
    options.onCustomFormsDataChange?.();
    cleanUpCustomFormsData();
  };

  const cleanUpCustomFormsData = (triggerId?: string): void => {
    activeTrigger.value = null;
    pendingStatusChange.value = null;
    if (triggerId !== undefined) {
      customFormsData.value[triggerId] = [];
    }
  };

  const checkForTriggerOnStatus = (
    status: string,
    triggersList: ITrigger[] | undefined
  ): ITrigger | undefined => {
    if (hasCustomFormsData.value || isDefined(pendingStatusChange.value)) {
      return undefined;
    }
    const entityName = CustomFormFlowEntitiesEnum.warehouse;
    const app = AppsEnum.Warehouse;
    let category: CustomFormFlowCategoryEnum | null = null;
    if (status === AppointmentStatus.Completed) {
      category = CustomFormFlowCategoryEnum.appointmentStatusCompleted;
    }
    if (status === AppointmentStatus.Arrived) {
      category = CustomFormFlowCategoryEnum.appointmentStatusArrived;
    }
    return triggersList?.find(
      (trigger: ITrigger & { isActive?: boolean }) =>
        (!app || trigger.app === app) &&
        (!entityName || trigger.entityName === entityName) &&
        trigger.category === category &&
        trigger.isActive !== false &&
        isDefined(trigger.flow)
    );
  };

  const getAppointmentRelatedAssetContainers = async (
    appointmentId: string
  ): Promise<unknown[]> => {
    if (!appointmentId) return [];
    const assetContainers = await assetVisitService.getAssetContainersByWarehouseId(null, {
      s: {
        $or: [{ pickupAppointmentId: appointmentId }, { dropAppointmentId: appointmentId }]
      },
      join: [] as string[]
    });
    return assetContainers ?? [];
  };

  return {
    activeTrigger,
    activeFlow,
    customFormsData,
    pendingStatusChange,
    hasActiveTrigger,
    hasCustomFormsData,
    customFieldComponents,
    customFormsComponentsMapping,
    getCustomField,
    getFormattedValue,
    createBulkCustomFormsData,
    updateCustomFormsData,
    handleActionFieldTriggered,
    customFormsDataChange,
    cleanUpCustomFormsData,
    checkForTriggerOnStatus,
    getCustomFormsData,
    getAppointmentRelatedAssetContainers
  };
}
